"use client";

import { ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reviews = [{
  id: 1,
  initials: "JD",
  name: "John D.",
  date: "[2024-05-12]",
  quote: "Price Advocate Services saved me thousands on my new vehicle purchase. Their negotiation tactics were professional and incredibly effective."
}, {
  id: 2,
  initials: "SK",
  name: "Sarah K.",
  date: "[2024-04-28]",
  quote: "I was overwhelmed by the home appliance quotes. The team stepped in, handled the vendors, and secured a much better deal than I could have."
}, {
  id: 3,
  initials: "MT",
  name: "Mark T.",
  date: "[2024-04-15]",
  quote: "Transparent, honest, and results-driven. They turned a stressful negotiation into a simple, successful transaction. Highly recommended."
}, {
  id: 4,
  initials: "EL",
  name: "Elena L.",
  date: "[2024-03-22]",
  quote: "They didn't just save me money; they saved me hours of frustration. Their expertise in large purchase advocacy is unmatched."
}, {
  id: 5,
  initials: "RB",
  name: "Robert B.",
  date: "[2024-02-19]",
  quote: "Professional service that pays for itself. The team at Price Advocate Services is my go-to for any major consumer purchase now."
}, {
  id: 6,
  initials: "AJ",
  name: "Alice J.",
  date: "[2024-01-10]",
  quote: "Clear communication and expert negotiation. I felt supported throughout the entire process. A truly valuable service for any consumer."
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);
  return <section data-section-id="1768" id="real-savings-stories" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="h-6 w-full" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(210 20% 94% / 0.5) 0 2px, transparent 2px 6px), repeating-linear-gradient(-45deg, hsl(210 20% 94% / 0.5) 0 2px, transparent 2px 6px)",
      backgroundColor: "var(--background)"
    }} aria-hidden="true" />

      <div ref={ref} className="container mx-auto max-w-[1360px] px-6 md:px-8 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className={`lg:sticky lg:top-24 transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="inline-block w-3 h-3 rounded-sm" style={{
                backgroundColor: "var(--brand-accent)"
              }} aria-hidden="true" />
                <span className="font-default text-xs font-bold uppercase" style={{
                color: "var(--brand-accent)",
                letterSpacing: "0.2em"
              }}>
                  Client Success Stories
                </span>
              </div>

              {}
              <h2 className="font-default font-bold uppercase text-primary" style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              lineHeight: "1.2",
              letterSpacing: "-0.02em"
            }}>
                Real Results For Real Consumers.
              </h2>

              {}
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="font-default font-bold uppercase text-foreground text-lg tracking-wide">
                    Client Feedback
                  </span>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2 h-2" style={{
                    backgroundColor: "var(--brand-accent)"
                  }} aria-hidden="true" />
                    <span className="font-default text-sm uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      5.0 / 5.0 Rating
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2 h-2" style={{
                    backgroundColor: "var(--brand-accent)"
                  }} aria-hidden="true" />
                    <span className="font-default text-sm uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      Verified Savings Success
                    </span>
                  </div>
                </div>

                {}
                <div className="pt-6">
                  <Link to="/AboutContactPage" className="group inline-flex items-center gap-2 font-default text-sm uppercase text-primary hover:text-secondary transition-colors duration-200" style={{
                  letterSpacing: "0.12em"
                }}>
                    <span>Contact Us Today</span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" style={{
                    color: "var(--brand-accent)"
                  }} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 min-w-0">
            <div className={`transition-all duration-500 ease-out delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Carousel opts={{
              align: "start",
              loop: false
            }} setApi={setApi} className="w-full">
                <CarouselContent className="-ml-4">
                  {reviews.map((review, i) => <CarouselItem key={review.id} data-index={i} className="pl-4 basis-full md:basis-1/2 lg:basis-[71%] xl:basis-[55%]">
                      <article className="relative h-full p-7 border rounded-lg shadow-sm" style={{
                    backgroundColor: "white",
                    borderColor: "var(--border)",
                    color: "var(--text-main)",
                    minHeight: "320px"
                  }}>
                        {}
                        <div className="flex flex-col h-full">
                          {}
                          <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                            {[0, 1, 2, 3, 4].map(s => <Star key={s} className="w-[18px] h-[18px]" style={{
                          color: "var(--brand-accent)",
                          fill: "var(--brand-accent)"
                        }} strokeWidth={1.5} />)}
                          </div>

                          {}
                          <p className="font-default mt-5 text-base leading-relaxed flex-1" style={{
                        color: "var(--text-main)"
                      }}>
                            &ldquo;{review.quote}&rdquo;
                          </p>

                          {}
                          <div className="h-px w-full my-5" style={{
                        backgroundColor: "var(--border)"
                      }} aria-hidden="true" />

                          {}
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg" style={{
                            backgroundColor: "var(--primary)",
                            color: "white"
                          }}>
                                <span className="font-default font-bold text-sm uppercase">
                                  {review.initials}
                                </span>
                              </div>
                              <span className="font-default font-bold text-sm truncate" style={{
                            color: "var(--text-main)"
                          }}>
                                {review.name}
                              </span>
                            </div>
                            <span className="font-default text-xs shrink-0" style={{
                          color: "var(--text-muted)"
                        }}>
                              {review.date}
                            </span>
                          </div>
                        </div>
                      </article>
                    </CarouselItem>)}
                </CarouselContent>
              </Carousel>

              {}
              <div className="flex items-center justify-end gap-3 mt-10">
                <Button variant="outline" onClick={() => api?.scrollPrev()} disabled={!canPrev} aria-label="Previous review" className="w-14 h-14 p-0 border-2 bg-transparent hover:bg-primary hover:text-white disabled:opacity-30 transition-colors duration-200 rounded-lg" style={{
                borderColor: "var(--primary)",
                color: "var(--primary)"
              }}>
                  <ChevronLeft className="w-6 h-6" strokeWidth={2} />
                </Button>
                <Button variant="outline" onClick={() => api?.scrollNext()} disabled={!canNext} aria-label="Next review" className="w-14 h-14 p-0 border-2 bg-transparent hover:bg-primary hover:text-white disabled:opacity-30 transition-colors duration-200 rounded-lg" style={{
                borderColor: "var(--primary)",
                color: "var(--primary)"
              }}>
                  <ChevronRight className="w-6 h-6" strokeWidth={2} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}